import React, { useState, useMemo, useEffect } from 'react';
import { getInventory, updateInventory, subscribeToInventory, unsubscribeChannel, logActivity } from '../../services/supabaseService';
import FilterBar from './components/FilterBar';
import InventoryTable from './components/InventoryTable';
import AssignTenantModal from './components/AssignTenantModal';
import UpdatePriceModal from './components/UpdatePriceModal';
import AddNoteModal from './components/AddNoteModal';
import BulkActionsBar from './components/BulkActionsBar';
import InventoryOnboardingWizard from './components/InventoryOnboardingWizard';
import Button from '../../components/ui/Button';


const InventoryManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [filters, setFilters] = useState({
    floor: 'all',
    unitType: 'all',
    status: 'all'
  });

  const [selectedUnits, setSelectedUnits] = useState([]);
  const [assignTenantModal, setAssignTenantModal] = useState({ isOpen: false, unit: null });
  const [updatePriceModal, setUpdatePriceModal] = useState({ isOpen: false, unit: null });
  const [addNoteModal, setAddNoteModal] = useState({ isOpen: false, unit: null });
  const [isOnboardingWizardOpen, setIsOnboardingWizardOpen] = useState(false);

  useEffect(() => {
    loadInventory();

    // Subscribe to real-time inventory changes
    const channel = subscribeToInventory(() => {
      loadInventory();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeChannel(channel);
    };
  }, []);

  const loadInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInventory();
      setInventory(data || []);
    } catch (err) {
      console.error('Inventory load error:', err);
      setError(err?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = useMemo(() => {
    return inventory?.filter(unit => {
      if (filters?.floor !== 'all' && unit?.floor !== filters?.floor) return false;
      if (filters?.unitType !== 'all' && unit?.unitType !== filters?.unitType) return false;
      if (filters?.status !== 'all' && unit?.status !== filters?.status) return false;
      return true;
    });
  }, [inventory, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ floor: 'all', unitType: 'all', status: 'all' });
  };

  const handleSelectUnit = (unitId, checked) => {
    setSelectedUnits(prev => 
      checked ? [...prev, unitId] : prev?.filter(id => id !== unitId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedUnits(checked ? filteredUnits?.map(u => u?.id) : []);
  };

  const handleMarkVacant = async (unitId) => {
    try {
      const unit = inventory?.find(u => u?.id === unitId);
      const oldStatus = unit?.status;
      
      await updateInventory(unitId, { status: 'vacant', tenantId: null });
      await loadInventory();
      
      // Log activity
      await logActivity({
        module: 'inventory',
        action: 'status_change',
        entityType: 'inventory',
        entityId: unitId,
        entityName: unit?.unitNumber || 'Unit',
        description: `Changed inventory status from ${oldStatus} to vacant`,
        oldValue: { status: oldStatus },
        newValue: { status: 'vacant' }
      });
    } catch (err) {
      console.error('Mark vacant error:', err);
      alert('Failed to mark unit as vacant');
    }
  };

  const handleMarkOccupied = async (unitId) => {
    try {
      const unit = inventory?.find(u => u?.id === unitId);
      const oldStatus = unit?.status;
      
      await updateInventory(unitId, { status: 'occupied' });
      await loadInventory();
      
      // Log activity
      await logActivity({
        module: 'inventory',
        action: 'status_change',
        entityType: 'inventory',
        entityId: unitId,
        entityName: unit?.unitNumber || 'Unit',
        description: `Changed inventory status from ${oldStatus} to occupied`,
        oldValue: { status: oldStatus },
        newValue: { status: 'occupied' }
      });
    } catch (err) {
      console.error('Mark occupied error:', err);
      alert('Failed to mark unit as occupied');
    }
  };

  const handleMarkMaintenance = async (unitId) => {
    try {
      const unit = inventory?.find(u => u?.id === unitId);
      const oldStatus = unit?.status;
      
      await updateInventory(unitId, { status: 'under_maintenance' });
      await loadInventory();
      
      // Log activity
      await logActivity({
        module: 'inventory',
        action: 'status_change',
        entityType: 'inventory',
        entityId: unitId,
        entityName: unit?.unitNumber || 'Unit',
        description: `Changed inventory status from ${oldStatus} to under maintenance`,
        oldValue: { status: oldStatus },
        newValue: { status: 'under_maintenance' }
      });
    } catch (err) {
      console.error('Mark maintenance error:', err);
      alert('Failed to mark unit as under maintenance');
    }
  };

  const handleAssignTenant = (unit) => {
    setAssignTenantModal({ isOpen: true, unit });
  };

  const handleUpdatePrice = async (unit) => {
    setUpdatePriceModal({ isOpen: true, unit });
  };

  const handleAddNote = (unit) => {
    setAddNoteModal({ isOpen: true, unit });
  };

  const handleTenantAssignment = async (data) => {
    try {
      await updateInventory(data?.unitId, { 
        tenantId: data?.tenantId,
        status: 'occupied'
      });
      await loadInventory();
      setAssignTenantModal({ isOpen: false, unit: null });
    } catch (err) {
      console.error('Tenant assignment error:', err);
      alert('Failed to assign tenant');
    }
  };

  const handlePriceUpdate = async (data) => {
    try {
      const updatePayload = {
        pricingModel: data?.pricingModel
      };

      // Update pricing fields based on pricing model
      if (data?.pricingModel === 'per_seat') {
        updatePayload.pricePerSeat = data?.pricePerSeat;
        updatePayload.pricePerSqft = null;
        updatePayload.fixedMonthlyPrice = null;
      } else if (data?.pricingModel === 'per_sqft') {
        updatePayload.pricePerSqft = data?.pricePerSqft;
        updatePayload.pricePerSeat = null;
        updatePayload.fixedMonthlyPrice = null;
      } else if (data?.pricingModel === 'fixed') {
        updatePayload.fixedMonthlyPrice = data?.fixedMonthlyPrice;
        updatePayload.pricePerSeat = null;
        updatePayload.pricePerSqft = null;
      }

      await updateInventory(data?.unitId, updatePayload);
      
      // Log activity
      await logActivity(
        'inventory_updated',
        'inventory',
        data?.unitId,
        `Updated pricing to ${data?.pricingModel} model`,
        { oldPricingModel: data?.oldPricingModel, newPricingModel: data?.pricingModel, notes: data?.notes }
      );

      await loadInventory();
      setUpdatePriceModal({ isOpen: false, unit: null });
    } catch (err) {
      console.error('Price update error:', err);
      alert('Failed to update price');
    }
  };

  const handleNoteAdd = async (data) => {
    try {
      await updateInventory(data?.unitId, { notes: data?.note });
      await loadInventory();
      setAddNoteModal({ isOpen: false, unit: null });
    } catch (err) {
      console.error('Note add error:', err);
      alert('Failed to add note');
    }
  };

  const handleBulkMarkVacant = async () => {
    try {
      await Promise.all(
        selectedUnits?.map(id => updateInventory(id, { status: 'vacant' }))
      );
      await loadInventory();
      setSelectedUnits([]);
    } catch (err) {
      console.error('Bulk mark vacant error:', err);
      alert('Failed to mark units as vacant');
    }
  };

  const handleBulkMarkOccupied = async () => {
    try {
      await Promise.all(
        selectedUnits?.map(id => updateInventory(id, { status: 'occupied' }))
      );
      await loadInventory();
      setSelectedUnits([]);
    } catch (err) {
      console.error('Bulk mark occupied error:', err);
      alert('Failed to mark units as occupied');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
        <p className="text-danger-800">Error: {error}</p>
        <button
          onClick={loadInventory}
          className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Inventory Management</h1>
          <p className="text-muted-foreground">Manage units, pricing, and tenant assignments</p>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button
            onClick={() => setIsOnboardingWizardOpen(true)}
            variant="primary"
            iconName="Upload"
            iconPosition="left"
          >
            Onboard Inventory
          </Button>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        resultCount={filteredUnits?.length}
      />

      <InventoryTable
        units={filteredUnits}
        onMarkVacant={handleMarkVacant}
        onMarkOccupied={handleMarkOccupied}
        onAssignTenant={handleAssignTenant}
        onUpdatePrice={handleUpdatePrice}
        onAddNote={handleAddNote}
        selectedUnits={selectedUnits}
        onSelectUnit={handleSelectUnit}
        onSelectAll={handleSelectAll}
      />

      <BulkActionsBar
        selectedCount={selectedUnits?.length}
        onBulkAction={(action) => {
          if (action === 'mark-vacant') handleBulkMarkVacant();
          if (action === 'mark-occupied') handleBulkMarkOccupied();
        }}
        onMarkVacant={handleBulkMarkVacant}
        onMarkOccupied={handleBulkMarkOccupied}
        onClearSelection={() => setSelectedUnits([])}
      />

      <AssignTenantModal
        isOpen={assignTenantModal?.isOpen}
        onClose={() => setAssignTenantModal({ isOpen: false, unit: null })}
        unit={assignTenantModal?.unit}
        existingTenants={[]}
        onAssign={handleTenantAssignment}
      />
      <UpdatePriceModal
        isOpen={updatePriceModal?.isOpen}
        onClose={() => setUpdatePriceModal({ isOpen: false, unit: null })}
        unit={updatePriceModal?.unit}
        onUpdate={handlePriceUpdate}
      />
      <AddNoteModal
        isOpen={addNoteModal?.isOpen}
        onClose={() => setAddNoteModal({ isOpen: false, unit: null })}
        unit={addNoteModal?.unit}
        onAdd={handleNoteAdd}
      />

      {/* Onboarding Wizard Modal */}
      {isOnboardingWizardOpen && (
        <InventoryOnboardingWizard
          centerId={inventory?.[0]?.centerId || 'CTR001'}
          onClose={() => setIsOnboardingWizardOpen(false)}
          onSuccess={() => {
            setIsOnboardingWizardOpen(false);
            loadInventory();
          }}
        />
      )}
    </div>
  );
};

export default InventoryManagement;